import { CoHostPersonality, ListenerProfile } from "types/studio.types";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/endPoints";

export interface Music {
  id: string;
  name: string;
  description: string;
  category: string;
  keywords: string;
  signedUrl: string;
  createdAt?: string;
  updatedAt?: string;
  runtime?: number;
}

/**
 * Generate a preview of how the studio configuration will sound with a specified article
 * @param websiteId The website ID
 * @param articleId The article ID to use for the preview
 * @param studioConfig The studio configuration including endUserProfile
 * @returns Preview data including script text and audio URL
 */
export const generateStudioPreview = async (
  websiteId: string,
  articleId: string,
  studioConfig: any,
  endUserProfile: any
) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.PREVIEW(websiteId), {
      articleIds: [{ articleId }],
      studioConfig,
      endUserProfile,
    });
    return response.data;
  } catch (error) {
    console.error("Error generating studio preview:", error);
    throw error;
  }
};

export const generateFullProgramPreview = async (listenerProfile: ListenerProfile) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.PUBLISHERS.FULL_PREVIEW, {
      listenerProfile
    });
    return response.data;
  } catch (error) {
    console.error("Error generating studio preview:", error);
    throw error;
  }
};

interface CastingDirectorResponse {
  voiceId: string;
  explanation: string;
};

export const invokeCastingDirector = async (
  websitePK: string,
  host: CoHostPersonality,
): Promise<CastingDirectorResponse> => {
  try {
    const response = await apiClient({
      method: 'post',
      url: API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.CASTING_DIRECTOR(websitePK),
      data: {
        websitePK,
        host,
      }
    });

    return response.data as CastingDirectorResponse;
  } catch (error) {
    console.error('Voice sample generation error:', error);
    throw error;
  }
};

export const AIGenerateStyle = async (websiteId: string, formatPreference: string) => {
  try {
    const url = API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.AI_STYLE(websiteId);
    const response = await apiClient.get(url, { params: { formatPreference } });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.error) {
      return { error: error.response.data.error };
    }
    console.error("Error generating style:", error);
    throw error;
  }
};

export const AIGenerateHosts = async (
  websiteId: string,
  audioStyleGuide: string,
  demographicProfile: string,
  formatPreference: string
) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.AI_HOSTS(websiteId), {
      audioStyleGuide,
      demographicProfile,
      formatPreference,
    });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.error) {
      return { error: error.response.data.error };
    }
    console.error("Error generating hosts:", error);
    throw error;
  }
};

export const GetMusic = async (websiteId: string) : Promise<Music[]>=> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.WEBSITES.STUDIO.MUSIC(websiteId));
    return response.data.items as Music[];
  } catch (error) {
    console.error("Error getting music:", error);
    throw error;
  }
};
